import React, { useRef, forwardRef, useImperativeHandle, useState, useEffect } from "react";
import { Box, Button, CheckboxField, Stack, TextField, Typography } from "@curves/ui-base";
import { getErrorModuleStatus, isEqual, isFunction, refIsRequiredError, isString } from "@util/utils";
import { Grid } from "@mui/material";
import CreditCardFields from "@icoach/components/creditCard/CreditCardFields";
import { SIGNTYPE, OWNERTEXT } from "@icoach/components/creditCard/creditCard.config";
import { EmployeeSelect, SignBox, UploadBox } from "@icoach/components";
import useFileUpload from "@icoach/components/creditCard/creditCardEditor/useFileUpload";
import { useMemberPaymentApi } from "@apis/useMemberPaymentApi";
import { useSnackbar } from "notistack";
import useCreditCardSubmitHandler from "@icoach/components/creditCard/creditCardEditor/useCreditCardSubmitHandler";
import DownloadCardAuthButton from "@icoach/components/creditCard/DownloadCardAuthButton";

const CreditCardEditor = forwardRef(
	(
		{
			sourceData = {
				cardRelation: null,
			},
			options,
			className,
			refresh,
		},
		ref,
	) => {
		const { enqueueSnackbar } = useSnackbar();
		
		// 表單欄位
		const cardFieldsRef = useRef(null);
		const ownerCardSignImgRef = useRef(null);
		const memberSignImgRef = useRef(null);
		const cardRelationRef = useRef(null);
		const noteRef = useRef(null);
		const employeeSelectRef = useRef(null);
		const [ownerType, setOwnerType] = useState(SIGNTYPE.DELEGATED);
		// 本人 or 非本人、卡片關係管理
		// 上傳檔案
		const { postMemberPaymentPluginApi, getMemberDefaultCreditCardApi } = useMemberPaymentApi();
		const { file: uploadedFile, setFile: setUploadedFile, onUpload: uploadAuthorizationFile } = useFileUpload(postMemberPaymentPluginApi);

		const formAdapter = {
			getFormData: () => {
				const cardRelation = cardRelationRef.current?.getResult();
				const signImg = isEqual(ownerType, SIGNTYPE.OWNER) ? ownerCardSignImgRef.current.getResult() : memberSignImgRef.current.getResult();
				return {
					...cardFieldsRef.current.getResult(),
					cardSignImg: ownerCardSignImgRef.current?.getResult() ?? "",
					signImg: signImg,
					note: noteRef.current?.getResult() ?? "",
					employeeID: employeeSelectRef.current.getResult(),
					cardRelation: isString(cardRelation) ? cardRelation : OWNERTEXT,
				};
			},
			validateForm: () => {
				return refIsRequiredError(cardFieldsRef, ownerCardSignImgRef, memberSignImgRef, cardRelationRef, noteRef, employeeSelectRef);
			},
			getFile: () => uploadedFile,
			showError: msg => {
				enqueueSnackbar(msg, { variant: "error" });
			},
		};

		const { onSubmit, checkError, validationErrors } = useCreditCardSubmitHandler({
			formAdapter,
			isPaperAuthorization: isEqual(ownerType, SIGNTYPE.OTHER),
			sourceData,
			uploadAuthorizationFile,
			refresh,
		});

		const handleApplyDefaultCreditCard = async () => {
			const resp = await getMemberDefaultCreditCardApi(sourceData?.memberID);

			if (!resp) return;

			const defaultCard = resp;

			const { updateFields } = cardFieldsRef.current || {};

			// 更新卡片欄位
			isFunction(updateFields) &&
				updateFields({
					creditCardNo: defaultCard.creditCardNo,
					cardExpiryDate: defaultCard.cardExpiryDate,
					creditCardBankName: defaultCard.creditCardBankName,
					cardName: defaultCard.cardName,
					creditCardType: defaultCard.creditCardType,
				});

			enqueueSnackbar("已套用常用信用卡資訊", { variant: "success" });
		};

		useImperativeHandle(
			ref,
			() => ({
				onSubmit,
				isError: checkError,
			}),
			// eslint-disable-next-line
			[onSubmit, checkError],
		);

		useEffect(
			() => {
				if (isEqual(sourceData.cardRelation, OWNERTEXT)) {
					setOwnerType(SIGNTYPE.OWNER);
				}
			},
			// eslint-disable-next-line
			[sourceData.cardRelation],
		);

		return (
			<Box className={className}>
				<Grid spacing={1} container>
					<Grid item xs={12}>
						<Box className={"btn-group"}>
							{!sourceData?.isMembershipCurrent && (
								<Button variant="outlined" onClick={handleApplyDefaultCreditCard}>
									套用常用信用卡
								</Button>
							)}
							<DownloadCardAuthButton />
						</Box>
					</Grid>
					<Grid item xs={12}>
						<CreditCardFields ref={cardFieldsRef} defaultValue={sourceData} errorModule={validationErrors} required />
					</Grid>
					<Grid xs={12} item>
						<Stack className="mt-2" spacing={1} direction={"row"} alignItems={"center"} justifyContent={"flex-start"}>
							{!isEqual(ownerType, SIGNTYPE.OTHER) && (
								<CheckboxField
									key={ownerType}
									defaultValue={[ownerType]}
									onChange={(_e, value) => {
										const type = value ? SIGNTYPE.OWNER : SIGNTYPE.DELEGATED;
										setOwnerType(type);
									}}
									row
								>
									<CheckboxField.Checkbox value={SIGNTYPE.OWNER} label={"信用卡為本人持有"} />
								</CheckboxField>
							)}
							{!isEqual(ownerType, SIGNTYPE.OWNER) && (
								<TextField
									label={"卡片關係為"}
									ref={cardRelationRef}
									defaultValue={sourceData.cardRelation !== OWNERTEXT ? sourceData.cardRelation : ""}
									{...getErrorModuleStatus(validationErrors, "cardRelation")}
									direction={isEqual(ownerType, SIGNTYPE.OTHER) ? "column" : "row"}
									required
								/>
							)}
						</Stack>
					</Grid>
					<Grid xs={8} item>
						{!isEqual(ownerType, SIGNTYPE.OWNER) && (
							<CheckboxField
								key={ownerType}
								defaultValue={[ownerType]}
								onChange={(_e, value) => {
									const type = value ? SIGNTYPE.OTHER : SIGNTYPE.DELEGATED;
									setOwnerType(type);
								}}
								row
							>
								<CheckboxField.Checkbox className={"text-error"} value={SIGNTYPE.OTHER} label={'卡片非本人，已填寫紙本"信用卡授權書"'} />
							</CheckboxField>
						)}
					</Grid>
					{isEqual(ownerType, SIGNTYPE.OTHER) && (
						<React.Fragment>
							<Grid xs={6} item>
								<UploadBox fileExtensions={["jpg", "png", "bmp"]} onFileChange={result => setUploadedFile(result?.file ?? null)} />
							</Grid>
							<Grid xs={6} item>
								<TextField label={"備註：(字數限字100字)"} ref={noteRef} maxLength={100} rows={4} multiline fullWidth />
							</Grid>
						</React.Fragment>
					)}
					{(isEqual(ownerType, SIGNTYPE.OWNER) || isEqual(ownerType, SIGNTYPE.DELEGATED)) && (
						<Grid xs={6} item>
							<SignBox title={"持卡人簽名"} ref={ownerCardSignImgRef} {...getErrorModuleStatus(validationErrors, "cardSignImg")} required />
							<Typography variant={"body2"} className={"font-color-3"}>
								(須與信用卡背面簽名一致)
							</Typography>
						</Grid>
					)}
					{!isEqual(ownerType, SIGNTYPE.OWNER) && (
						<Grid xs={6} item>
							<SignBox title={"會員簽名"} ref={memberSignImgRef} {...getErrorModuleStatus(validationErrors, "signImg")} required />
						</Grid>
					)}
					<Grid xs={6} item>
						<EmployeeSelect label="擔當" ref={employeeSelectRef} options={options?.employeeOption} fullWidth required />
					</Grid>
				</Grid>
			</Box>
		);
	},
);

export default CreditCardEditor;
